/**
 * Invoice Export Modal
 * ====================
 * Modal for selecting date range and exporting invoices to Excel.
 * Invoices are grouped by month into separate sheets.
 */

import React, { useState, useCallback } from 'react';
import { useLang } from '../../shared/i18n';

type DateRangePreset = '1m' | '3m' | '6m' | '9m' | '1y' | 'custom';

export interface DateRange {
    startDate: Date;
    endDate: Date;
    preset: DateRangePreset;
}

interface InvoiceExportModalProps {
    isOpen: boolean;
    onClose: () => void;
    onExport: (dateRange: DateRange) => void;
    isLoading?: boolean;
}

const PRESET_DEFS: { value: DateRangePreset; labelKey: string }[] = [
    { value: '1m', labelKey: 'preset1m' },
    { value: '3m', labelKey: 'preset3m' },
    { value: '6m', labelKey: 'preset6m' },
    { value: '9m', labelKey: 'preset9m' },
    { value: '1y', labelKey: 'preset1y' },
    { value: 'custom', labelKey: 'presetCustom' },
];

const getDateRangeFromPreset = (preset: DateRangePreset): { start: Date; end: Date } => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date();
    start.setHours(0, 0, 0, 0);

    switch (preset) {
        case '1m': start.setMonth(start.getMonth() - 1); break;
        case '3m': start.setMonth(start.getMonth() - 3); break;
        case '6m': start.setMonth(start.getMonth() - 6); break;
        case '9m': start.setMonth(start.getMonth() - 9); break;
        case '1y': start.setFullYear(start.getFullYear() - 1); break;
        default: start.setMonth(start.getMonth() - 1);
    }
    return { start, end };
};

const formatDateForInput = (date: Date): string => date.toISOString().split('T')[0];

const InvoiceExportModal: React.FC<InvoiceExportModalProps> = ({ isOpen, onClose, onExport, isLoading = false }) => {
    const { t } = useLang();
    const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>('3m');
    const [customStartDate, setCustomStartDate] = useState<string>(() => {
        const d = new Date(); d.setMonth(d.getMonth() - 1); return formatDateForInput(d);
    });
    const [customEndDate, setCustomEndDate] = useState<string>(formatDateForInput(new Date()));

    const handlePresetChange = useCallback((preset: DateRangePreset) => {
        setSelectedPreset(preset);
        if (preset !== 'custom') {
            const { start, end } = getDateRangeFromPreset(preset);
            setCustomStartDate(formatDateForInput(start));
            setCustomEndDate(formatDateForInput(end));
        }
    }, []);

    const handleExport = useCallback(() => {
        let startDate: Date, endDate: Date;
        if (selectedPreset === 'custom') {
            startDate = new Date(customStartDate); startDate.setHours(0, 0, 0, 0);
            endDate = new Date(customEndDate); endDate.setHours(23, 59, 59, 999);
        } else {
            const range = getDateRangeFromPreset(selectedPreset);
            startDate = range.start; endDate = range.end;
        }
        onExport({ startDate, endDate, preset: selectedPreset });
    }, [selectedPreset, customStartDate, customEndDate, onExport]);

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget && !isLoading) onClose();
    };

    if (!isOpen) return null;

    const presetRange = getDateRangeFromPreset(selectedPreset);
    const displayStartDate = selectedPreset === 'custom' ? customStartDate : formatDateForInput(presetRange.start);
    const displayEndDate = selectedPreset === 'custom' ? customEndDate : formatDateForInput(presetRange.end);

    return (
        <div className="invoice-export-modal-overlay" onClick={handleBackdropClick}>
            <div className="invoice-export-modal">
                <div className="invoice-export-modal__header">
                    <h2>{t('exportTitle')}</h2>
                    <button className="invoice-export-modal__close" onClick={onClose} disabled={isLoading}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
                <div className="invoice-export-modal__body">
                    <p className="invoice-export-modal__description">
                        {t('exportDescription')}<br />
                        <strong>{t('exportSheetNote')}</strong>
                    </p>
                    <div className="invoice-export-modal__presets">
                        {PRESET_DEFS.map((option) => (
                            <button key={option.value} className={`preset-btn ${selectedPreset === option.value ? 'active' : ''}`}
                                onClick={() => handlePresetChange(option.value)} disabled={isLoading}>{t(option.labelKey)}</button>
                        ))}
                    </div>
                    {selectedPreset === 'custom' && (
                        <div className="invoice-export-modal__custom-dates">
                            <div className="date-input-group">
                                <label>{t('startDate')}</label>
                                <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} max={customEndDate} disabled={isLoading} />
                            </div>
                            <div className="date-input-group">
                                <label>{t('endDate')}</label>
                                <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} min={customStartDate} max={formatDateForInput(new Date())} disabled={isLoading} />
                            </div>
                        </div>
                    )}
                    <div className="invoice-export-modal__summary">
                        <span className="summary-label">{t('selectedRange')}</span>
                        <span className="summary-value">
                            {new Date(displayStartDate).toLocaleDateString('tr-TR')} - {new Date(displayEndDate).toLocaleDateString('tr-TR')}
                        </span>
                    </div>
                </div>
                <div className="invoice-export-modal__footer">
                    <button className="btn-cancel" onClick={onClose} disabled={isLoading}>{t('cancel')}</button>
                    <button className="btn-export" onClick={handleExport} disabled={isLoading}>
                        {isLoading ? (<><span className="spinner-small" />{t('preparing')}</>) : (
                            <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                            </svg>{t('downloadExcel')}</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InvoiceExportModal;
